// File: components/cart/cart-content.tsx
"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";
import { ShoppingCart } from "lucide-react";
import type { SellerCheckoutInfo, SellerGroup } from "./types";
import { SellerGroupComponent } from "./seller-group";
import { dedupAvailabilityRequest } from "@/lib/cart-request-deduplication";
import { countGroupsPerSeller, type CheckoutGroupIdentity } from "@/lib/cart/checkout-groups";
import { supabaseClientRealtime as supabase } from "@/utils/supabase/clientRealtime";
import { realtimeManager } from "@/utils/realtime/manager";
import { cn } from "@/lib/utils";

interface Availability { [listingId: string]: number }

interface CartContentProps {
  cartItems: any[];
  isMobile: boolean;
  compact?: boolean;
  updateQuantityAction: (id: string, qty: number) => void;
  removeItemAction: (id: string) => void;
  updateShippingMethodAction: (id: string, shippingId: string) => void;
  refreshListingShippingMethodsAction?: (listingId: string) => void | Promise<void>
  activeSwipeItemId: string | null;
  setActiveSwipeItemIdAction: (id: string | null) => void;
  itemsBySeller: Record<string, SellerGroup>;
  userId?: string | null;
  onAvailabilityChange?: (availability: Availability) => void;
  controlsDisabled?: boolean;
  subtotal: number;
  shippingTotal: number;
  serviceFee: number;
  // NOTE: no `currencyCode` prop. It existed only to rebuild the
  // `sellerId:currencyCode` lookup key by hand; each group now carries its own
  // `checkoutGroup` ref, so the join is a lookup and the currency travels with
  // the data instead of alongside it.
  sellerValidationErrors?: Record<string, string>;
  availabilityRefreshKey?: number;
  // Per-checkout-group props (keyed by `CheckoutGroupKey`, NOT by seller id —
  // one seller can own two groups when their listings mix shipping modes)
  sellerCheckoutInfo?: Record<string, SellerCheckoutInfo>;
  onSellerCheckout?: (group: CheckoutGroupIdentity) => void;
  // In-flight reservation tracking (a `CheckoutGroupKey` or null)
  creatingReservationsFor?: string | null;
  onLogin?: () => void;
  onRegister?: () => void;
}

export function CartContent({
  cartItems,
  isMobile,
  compact = false,
  updateQuantityAction,
  removeItemAction,
  updateShippingMethodAction,
  refreshListingShippingMethodsAction,
  activeSwipeItemId,
  setActiveSwipeItemIdAction,
  itemsBySeller,
  userId,
  onAvailabilityChange,
  controlsDisabled,
  subtotal,
  shippingTotal,
  serviceFee,
  sellerValidationErrors = {},
  availabilityRefreshKey = 0,
  sellerCheckoutInfo,
  onSellerCheckout,
  creatingReservationsFor = null,
  onLogin,
  onRegister,
}: CartContentProps) {
  const t = useTranslations("cartContent");
  const [availability, setAvailability] = useState<Availability>({});
  const refreshTimer = useRef<NodeJS.Timeout | null>(null);
  const cleanupRealtimeRef = useRef<() => void>(() => {});
  const prevRefreshKeyRef = useRef(availabilityRefreshKey);

  const sortedListingIds = React.useMemo(
    () => Array.from(new Set(cartItems.map(i => i.listingId))).sort().join(','),
    [cartItems]
  );

  // Global scroll handler to remove focus from any active element on mobile
  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      // Remove focus from any active element when scrolling
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    // Listen to all scroll and touch events
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isMobile]);

  useEffect(() => {
    if (!sortedListingIds) {
      setAvailability({});
      return;
    }

    const ids = sortedListingIds.split(",").filter(Boolean)

    // Bypass dedup cache when refreshKey changes (e.g. cart reopened)
    const forceRefresh = availabilityRefreshKey !== prevRefreshKeyRef.current;
    prevRefreshKeyRef.current = availabilityRefreshKey;

    dedupAvailabilityRequest(sortedListingIds, async () => {
      const params = new URLSearchParams();
      ids.forEach((id) => params.append("listingId", id));

      const response = await fetch(`/api/cart/availability?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Availability API failed: ${response.status}`);
      }

      return response.json();
    }, !forceRefresh)
      .then((arr: Array<{ listingId: string; available: number }>) => {
        const map: Availability = {};
        arr.forEach(({ listingId, available }) => {
          map[listingId] = available;
        });
        setAvailability(map);
        onAvailabilityChange?.(map); // Notifier le parent
      })
      .catch((error) => {
        console.error("Failed to fetch cart availability:", error);
        setAvailability({});
      });
  }, [sortedListingIds, onAvailabilityChange, availabilityRefreshKey]);

  // Realtime: refresh availability when reservations or listing stock change
  useEffect(() => {
    if (cartItems.length === 0) return;

    // Build stable set of listing ids
    const ids = Array.from(new Set(cartItems.map(i => i.listingId))).sort();

    // Helper to trigger debounced refresh
    const triggerRefresh = () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        const params = new URLSearchParams();
        ids.forEach(id => params.append("listingId", id));
        fetch(`/api/cart/availability?${params.toString()}`)
          .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
          .then((arr: Array<{ listingId: string; available: number }>) => {
            const map: Availability = {};
            arr.forEach(({ listingId, available }) => { map[listingId] = available; });
            setAvailability(map);
            onAvailabilityChange?.(map);
          })
          .catch(() => {});
      }, 200); // debounce bursty updates
    };

    // Subscribe to item_reservations and listings (Postgres changes)
    const channel = supabase.channel(`cart-availability-${ids.join(',')}`);
    const handler = (payload: any) => {
      if (process.env.NODE_ENV !== 'production') {
        try { console.debug('[cart realtime] change', payload); } catch {}
      }
      const rec = (payload?.new ?? payload?.record ?? {}) as any;
      const old = (payload?.old ?? payload?.old_record ?? {}) as any;
      const changedListingId = rec.listing_id ?? rec.id ?? old.listing_id ?? old.id;
      if (!changedListingId) return;
      if (ids.includes(changedListingId)) triggerRefresh();
    };
    channel
      .on('postgres_changes', { event: '*', schema: 'public', table: 'item_reservations' }, handler)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'listings' }, handler)
      .subscribe();

    cleanupRealtimeRef.current = () => { try { supabase.removeChannel(channel); } catch {} };

    // Additionally, listen to broadcast-based inventory notifications (same model as chat)
    const cleanupBroadcast = realtimeManager.subscribeToRooms(["inventory"], (_room, payload) => {
      const listingIds = (payload?.payload?.listingIds ?? []) as string[];
      if (listingIds.some(id => ids.includes(id))) triggerRefresh();
    });

    return () => {
      if (refreshTimer.current) { clearTimeout(refreshTimer.current); refreshTimer.current = null; }
      cleanupRealtimeRef.current?.();
      try { cleanupBroadcast(); } catch {}
    };
  }, [sortedListingIds, onAvailabilityChange]);
  if (cartItems.length === 0) {
    const emptyCls = "text-foreground";
    const emptyMutedCls = "text-muted-foreground";
    return (
      <div className={`flex flex-col items-center justify-center p-8 text-center h-[200px] ${emptyCls}`}>
        <ShoppingCart className={cn("h-12 w-12 mb-4", emptyMutedCls)} />
        <p>{t("empty")}</p>
        <p className={cn("mt-4 text-sm italic", emptyMutedCls)}>
          {t("emptySubtitle")}
        </p>
      </div>
    );
  }

  // `isMobile` IS the sheet signal here: the mobile branch of `cart.tsx`
  // renders this inside the `DockSheet` panel, the desktop branch inside the
  // hand-rolled drawer on a `bg-card` shell. That is why it also drives the
  // `onSheet` surface prop handed to `SellerGroupComponent` below — which is
  // no longer a "paint it dark" flag but a choice of colour LADDER plus
  // geometry, live in both directions (step 2c-ter).
  // @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §4 (step 2c)
  const compactMode = compact || isMobile;
  const contentPx = compactMode ? "px-4" : "px-2 md:px-4";
  const gapCls = compactMode ? "gap-4" : "gap-6";
  const groups = Object.values(itemsBySeller);
  // One seller can now own more than one card (mixed shipping modes ship as
  // separate orders). Count first so each card can SAY so — two cards under
  // the same seller name otherwise read as a duplicate-render bug.
  const groupsPerSeller = countGroupsPerSeller(groups.map((g) => g.checkoutGroup));
  return (
    <div className={cn("grid pb-4", contentPx, gapCls)}>
      {groups.map((group) => {
        // Lookup, not string-concat: the group carries its own canonical key
        // (seller × currency × shipping mode), so this join can never drift
        // from `buildCheckoutGroupKey`.
        const checkoutInfo = sellerCheckoutInfo?.[group.checkoutGroup.key];

        return (
          <SellerGroupComponent
            key={group.checkoutGroup.key}
            group={group}
            splitFromSameSeller={(groupsPerSeller[group.checkoutGroup.sellerId] ?? 1) > 1}
            isMobile={isMobile}
            onSheet={isMobile}
            compact={compactMode}
            updateQuantityAction={updateQuantityAction}
            removeItemAction={removeItemAction}
            updateShippingMethodAction={updateShippingMethodAction}
            refreshListingShippingMethodsAction={refreshListingShippingMethodsAction}
            activeSwipeItemId={activeSwipeItemId}
            setActiveSwipeItemIdAction={setActiveSwipeItemIdAction}
            availability={availability}
            controlsDisabled={!!controlsDisabled}
            sellerValidationError={sellerValidationErrors[group.seller.id]}
            checkoutInfo={checkoutInfo}
            onCheckout={onSellerCheckout}
            creatingReservationsFor={creatingReservationsFor}
            userId={userId}
            onLogin={onLogin}
            onRegister={onRegister}
          />
        );
      })}
    </div>
  );
}
